import React, { useEffect, useRef, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import * as cryptoJS from "crypto-js";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { NotificationToast } from "../../../components/Generic/Toast/NotificationToast";
import { CUSTOMER_PORTAL_PUBLIC_ROUTES } from "../../../constants";
import { useAppState } from "../../../context/StateContext";
import { useSecretManager } from "../../../hooks/useSecretManager";
import CustomerPortalService from "../../../services/customer.portal.service";
import {
  checkTokenCustomerInvite,
  ForgotPasswordToken,
} from "../../../util/checkTokenCustomerInvite";
import deleteAllTokens from "../../../util/deleteAllTokens";
import env from "../../../util/env";
import { getTokenCustomerPortalByWindowLocation } from "../../../util/getTokenCustomerPortalByWindowLocation";
import { CustomerPortalFooter } from "../../private/Moving/CustomerPortal/CustomerPortalFooter";
import UpdatePasswordForm, { UpdatePasswordFormHandler } from "./Form";
import { ContainerFooter, PageWrapper } from "./styles";
import UpdatedScreen from "./UpdatedScreen";

interface UserDataToEncrypt {
  id: number;
  password: string;
  confirmPassword: string;
}

const CustomerPortalUpdatePassword = () => {
  const [customerUserId, setCustomerUserId] = useState<number>();
  const [passwordUpdated, setPasswordUpdated] = useState<boolean>(false);

  const history = useHistory();

  const { secret } = useSecretManager(env.SECRET_ID);

  const updatePasswordFormRef = useRef<UpdatePasswordFormHandler>(null);

  const { t: translate } = useTranslation();

  const { setCurrentCustomerUser, currentCustomerUser } = useAppState();

  const { mutate: recoverPassword, isLoading } = useMutation(
    ["update.password.customer.portal"],
    (recoverPasswordData: string) => CustomerPortalService.recoverPassword(recoverPasswordData),
    {
      onSuccess: (response) => {
        if (response && response.data) {
          setPasswordUpdated(true);
          setTimeout(() => {
            if (currentCustomerUser) {
              deleteAllTokens();
              setCurrentCustomerUser(undefined);
            }
            history.replace("/");
          }, 5000);
        }
      },
      onError: () => {
        NotificationToast.error(translate("anErrorOccurredTryAgain"));
      },
    }
  );

  useEffect(() => {
    const token = getTokenCustomerPortalByWindowLocation();

    if (!currentCustomerUser?.customerId) {
      if (token) {
        if (secret && secret?.SECRET_KEY_PASSWORD) {
          const decryptedUserData = checkTokenCustomerInvite<ForgotPasswordToken>(
            token,
            secret?.SECRET_KEY_PASSWORD
          );

          if (decryptedUserData) {
            setCustomerUserId(decryptedUserData.customerUserId || 0);
          } else {
            NotificationToast.warning(translate("theTokenProvidedIsIncorrect"));
            history.replace(CUSTOMER_PORTAL_PUBLIC_ROUTES.FORGOT);
          }
        }
      } else {
        history.replace(CUSTOMER_PORTAL_PUBLIC_ROUTES.FORGOT);
      }
    }
  }, [secret, currentCustomerUser?.customerId]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!secret || !secret?.SECRET_KEY_PASSWORD) {
      NotificationToast.error(translate("anUnknownErrorHasOccurred"));

      return;
    }

    const userDataToEncrypt: UserDataToEncrypt = {
      id: currentCustomerUser?.id || customerUserId || 0,
      password: updatePasswordFormRef?.current?.newPassword || "",
      confirmPassword: updatePasswordFormRef?.current?.confirmPassword || "",
    };

    const userDataEncrypted = cryptoJS.AES.encrypt(
      JSON.stringify(userDataToEncrypt),
      secret.SECRET_KEY_PASSWORD
    ).toString();

    recoverPassword(userDataEncrypted);
  };

  return (
    <PageWrapper>
      {passwordUpdated ? (
        <UpdatedScreen />
      ) : (
        <UpdatePasswordForm
          ref={updatePasswordFormRef}
          handleSubmit={handleSubmit}
          isLoading={isLoading}
        />
      )}
      <ContainerFooter>
        <CustomerPortalFooter center />
      </ContainerFooter>
    </PageWrapper>
  );
};

export default CustomerPortalUpdatePassword;
